import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import {
  Injector,
  enableProdMode,
  importProvidersFrom,
  provideAppInitializer,
} from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_RIPPLE_GLOBAL_OPTIONS,
} from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  TranslateCompiler,
  TranslateLoader,
  TranslateModule,
} from '@ngx-translate/core';
import { NgxsDispatchPluginModule } from '@ngxs-labs/dispatch-decorator';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsLoggerPluginModule } from '@ngxs/logger-plugin';
import {
  NgxsStoragePluginModule,
  SESSION_STORAGE_ENGINE,
} from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { OktaAuthModule } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';
import {
  getInitConfigFactory,
  initAccessibilityObserversFactory,
} from '@sc/data-access-app-injector';
import { AssetsService } from '@sc/data-access-assets';
import { DataAccessEnvironmentModule } from '@sc/data-access-environment';
import {
  CLIENT_APP_ID,
  HIGHLIGHT_REQUIRED_INPUTS,
} from '@sc/data-access-models';
import {
  AppointmentsFilterState,
  AppointmentsState,
  CTAQueryState,
  DossierState,
  DotFittersState,
  FilesState,
  FittersState,
  NavigationState,
  ScoresState,
} from '@sc/data-access-state-management';
import {
  CustomLangLoaderService,
  CustomMatPaginatorIntl,
  LocaleProvider,
  TabTitleStrategyService,
} from '@sc/data-access-translation';
import { FeatureAuthOktaModule } from '@sc/feature-auth-okta';
import { CustomerRouterService } from '@sc/feature-path-router-system';
import {
  CustomTagManagerService,
  GTagManagerService,
  MatomoService,
  TAG_MANAGER,
} from '@sc/feature-tag-manager';
import { ThemesManagerService } from '@sc/feature-themes-manager';
import {
  CUSTOM_DATE_FORMATS,
  DayjsMaterialDateAdapterService,
} from '@sc/ui-components';
import { TranslateMessageFormatCompiler } from 'ngx-translate-messageformat-compiler';
import { of } from 'rxjs';
import { AppRoutingModule } from './app/app-routing.module';
import { AppComponent } from './app/app.component';
import { GraphQLModule } from './app/graphql.module';
import { environment } from './environments/environment';
import { MAT_SELECT_CONFIG } from '@angular/material/select';
import { HttpErrorInterceptor } from '@sc/feature-script-manager';

const oktaAuth = new OktaAuth(environment.auth);

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      AppRoutingModule,
      GraphQLModule,
      DataAccessEnvironmentModule.forRoot(environment),
      OktaAuthModule.forRoot({ oktaAuth }),
      FeatureAuthOktaModule.forRoot({
        envAuth: environment.auth,
        guardRedirectUrl: '/unauthorized',
      }),
      NgxsStoragePluginModule.forRoot({
        keys: [{ key: CTAQueryState, engine: SESSION_STORAGE_ENGINE }],
      }),
      NgxsModule.forRoot(
        [
          FittersState,
          CTAQueryState,
          NavigationState,
          ScoresState,
          AppointmentsState,
          DossierState,
          AppointmentsFilterState,
          FilesState,
          DotFittersState,
        ],
        {
          developmentMode:
            String(environment.log.logActivationStateManagement) === 'true',
        }
      ),
      NgxsDispatchPluginModule.forRoot(),
      NgxsLoggerPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      NgxsReduxDevtoolsPluginModule.forRoot({
        disabled:
          String(environment.log.logActivationStateManagement) !== 'true',
      }),
      TranslateModule.forRoot({
        compiler: {
          provide: TranslateCompiler,
          useClass: TranslateMessageFormatCompiler,
        },
        loader: { provide: TranslateLoader, useClass: CustomLangLoaderService },
      }),
      MatSnackBarModule,
      MatDialogModule,
      MatBottomSheetModule
    ),
    LocaleProvider,
    { provide: CLIENT_APP_ID, useValue: 'customer' },
    { provide: HIGHLIGHT_REQUIRED_INPUTS, useValue: false },
    // { provide: B2C_ROUTER_VERBOSE_TOKEN, useValue: B2CRouterVerbose.Debug },
    provideAppInitializer(() => {
      const initializerFn = getInitConfigFactory((injector: Injector) => {
        // Force init of following services
        injector.get(AssetsService);
        injector.get(ThemesManagerService);
        injector.get(CustomerRouterService);
        // Remove tabTitleStrategy after its refacto in angular 14, until then we want it to be eagerly loaded at app init
        injector.get(TabTitleStrategyService);
        return of(null);
      })();
      return initializerFn();
    }),
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    provideAppInitializer(() => {
      const initializerFn = initAccessibilityObserversFactory();
      return initializerFn();
    }),
    // NOTE - If we find how, it would be interesting to lazy-load the tag managers depending on config
    {
      provide: TAG_MANAGER,
      useExisting: GTagManagerService,
      multi: true,
    },
    {
      provide: TAG_MANAGER,
      useClass: CustomTagManagerService,
      multi: true,
    },
    {
      provide: TAG_MANAGER,
      useExisting: MatomoService,
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: DayjsMaterialDateAdapterService,
    },
    {
      provide: MAT_DATE_FORMATS,
      useValue: CUSTOM_DATE_FORMATS,
    },
    {
      provide: MAT_RIPPLE_GLOBAL_OPTIONS,
      useValue: {
        disabled: true,
        animation: {
          enterDuration: 0,
          exitDuration: 0,
        },
      },
    },
    {
      provide: MAT_SELECT_CONFIG,
      useValue: { overlayPanelClass: 'select-custom-overlay-pane' },
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorInterceptor,
      multi: true, // important : permet d'avoir plusieurs intercepteurs
    },

    provideHttpClient(withInterceptorsFromDi()),
    provideAnimations(),
  ],
}).catch((err) => console.error(err));
